import { Encoding } from "../js/encoding.js";
import { getToken } from "./common.js";
import { CLIENT_ID, REDIRECT_URL, URI } from "../config/autodesk.config.js"
import { UrlBuilder } from "../js/UrlBuilder.js";

class CodeVerifier {
  static _storageKey = "code_verifier";
  static get() { return sessionStorage.getItem(CodeVerifier._storageKey); }
  static del() { sessionStorage.removeItem(CodeVerifier._storageKey); }
  static _generate() { return Encoding.generateRandomString(28) }

  constructor() {
    if (CodeVerifier.get() === null || CodeVerifier.get() === undefined) {
      sessionStorage.setItem(CodeVerifier._storageKey, CodeVerifier._generate());
    }
  }

  toCodeChallenge() {
    return Encoding.sha256(CodeVerifier.get()).then(Encoding.base64urlencode);
  }
}

class Auth3L {
  /* Authenticate using a 3-legged process with PKCE
   * https://aps.autodesk.com/en/docs/oauth/v2/tutorials/get-3-legged-token-pkce/
   */
  static _localStorageKey = "access_token";

  static authenticate(scopes, state) {
    /** 
     * Initiates the login process by redirect user to autodesk login page.
     * @param {Object[]} props.scopes - Array of permission scopes the assigned access token will inherit
     *                                  (see https://aps.autodesk.com/en/docs/oauth/v2/developers_guide/scopes/).
     * @param {string}   [props.state] - Optional string to be passed back as a query parameter to the redirect URL.
     */
    const access_code = new URL(window.location.href).searchParams.get('code');
    if ( access_code === undefined || access_code === null ) {
      CodeVerifier.del();
      new CodeVerifier().toCodeChallenge()
          .then(codeChallenge => {
            const scope = [...scopes, 'user-profile:read'].join(' ');
            window.location.href = Auth3L._loginUrl({state, scope, codeChallenge});
        }).catch(err => {
          throw new Error(`Error generating code challenge: ${err}`)
        });
    }
  }

  static _loginUrl({state, scope, codeChallenge}) {
    let url = new UrlBuilder(URI)
        .set_pathname('/authentication/v2/authorize')
        .with_query('response_type', 'code')
        .with_query('client_id', CLIENT_ID)
        .with_query('redirect_uri', REDIRECT_URL)
        .with_query('scope', scope)
        .with_query('nonce', '123456')
        .with_query('prompt', 'login')
        .with_query('code_challenge', codeChallenge)
        .with_query('code_challenge_method', 'S256');
    if (typeof state === 'string') {
      url.with_query('state', state)
    }  
    return url.as_href();
  }

  static publicCodeTokenExchange(code, verifier) {
    /** To be used by a public client. App client-secret is not used.
     *  Has limited access to API's
     */
    const payload = {
      'client_id': CLIENT_ID,
      'redirect_uri': REDIRECT_URL,
      'grant_type': 'authorization_code',
      'code_verifier': verifier,
      'code': code,
    }
    return getToken(payload);
  }
  
  static authenticated(exchangeFunction) {
    /**
     * Check to see if current session is authenticated.
     * @param {Promise(code, verifier)} exchangeFunction - async function to exchanges an access code for a token
     *    For client side authentication use `publicCodeTokenExchange` from common.js.
     *    For Server side authentication you must provide a custom implementation.
     */
    return new Promise((resolve, reject) => {
      const accessCode = new URL(window.location.href).searchParams.get('code');
      if (Auth3L.get_token() !== null && Auth3L.get_token() !== undefined) {
        Auth3L.user()
            .then((r) => { resolve(r !== null); })
            .catch(reject);
      } else if (accessCode !== undefined && accessCode !== null) {
        // User is authenticated but does not have access token
        exchangeFunction(accessCode, CodeVerifier.get())
            .then(accessToken => {
              Auth3L.save_token(accessToken);
              resolve(true);
            }).catch(err => {
              reject(new Error(`Error exchanging access_code for token: ${err}`));
            });
      } else {
        resolve(false);
      }
    });
  }

  static user() {
    /**
     * Get information about the authenticated user.
     */
    return fetch('https://api.userprofile.autodesk.com/userinfo', {
        headers: {
          Authorization: Auth3L.get_token(),
        }
      })
      .then((r) => {
        if (! r.ok) { return null; }
        return r.json()
          .then((r) => {
            let firstName = r.given_name;
            let lastName = r.family_name;
            // If user does not have <given_name> or <family_name> populated, set last name to 
            // the <name> field and first name to empty string.
            const isEmpty = (field) => (typeof field !== 'string' || field.length === 0);
            if (isEmpty(r.given_name) && isEmpty(r.family_name)) {
              firstName = ''; lastName = r.name;
            }
            return {firstName, lastName};
          });
    });
  }

  static clear()           { localStorage.removeItem(Auth3L._localStorageKey); }
  static get_token()       { return localStorage.getItem(Auth3L._localStorageKey); }
  static save_token(token) { localStorage.setItem(Auth3L._localStorageKey, token); }
}

export { Auth3L }
